<script setup lang="ts">
// @ts-nocheck — stages prop uses a union type whose index signature is not fully inferred
import { ref, computed } from "vue";
import Table from "@/components/Reusables/Table/Table.vue";
import { KeyedIdentityEnum } from "@/providers/Materials/LibraryEnumsProvider";
import { LCIAResult } from "@/types/Views/MaterialDatabase/Material";
import type { LifeCycleStage } from "@/stores/project/types/enum.ts";
import format from "@/services/Utils/format";

const props = defineProps<{
  lciaResults: LCIAResult[];
  stages: KeyedIdentityEnum | LifeCycleStage;
}>();

const hoverSet = ref<[string | null, string | null]>([null, null]);

const hoveredPhase = computed(() => hoverSet.value[0]);
const hoveredIndicator = computed(() => hoverSet.value[1]);

const onHover = (phase: string | null, indicator: string | null) => {
  hoverSet.value = [phase, indicator];
};

const extractUnit = (identifier: string) =>
  props.lciaResults[0].indicators.find((indicator) => indicator.originalIdentifier === identifier)?.unit;

const getStage = (moduleTypeEnum: number): string | undefined =>
  props.stages[moduleTypeEnum]?.DisplayName?.replace("A1A3", "A1-A3");

const indicators = computed(() =>
  Array.from(
    new Set(
      props.lciaResults
        .filter((result) => result.moduleStatusEnum === 2)
        .flatMap((result) => result.indicators.map((indicator) => indicator.originalIdentifier))
        .filter((identifier) => identifier !== undefined)
    )
  )
);

const results = computed(() =>
  props.lciaResults.filter((result) => ![1, 2, 3].includes(result.moduleTypeEnum) && result.moduleStatusEnum === 2)
);

const getIndicatorValue = (result: LCIAResult, indicator: string) =>
  result.indicators.find((item) => item.originalIdentifier === indicator)?.value;
</script>

<template>
  <Table
    caption="LCIA results by stage and indicator"
    class="scrollbar pb-px-12 flex grow-0 flex-col overflow-x-auto"
    table-class="text-xs"
    @mouseleave="onHover(null, null)">
    <template #thead>
      <thead>
        <tr>
          <th
            scope="col"
            class="z-sticky w-px-128 px-px-4 py-px-2 t-xs sticky left-0 bg-slate-600 text-left font-bold text-white dark:bg-slate-300">
            Indicator
          </th>
          <th
            scope="col"
            class="w-px-128 px-px-4 py-px-2 t-xs bg-slate-600 text-left font-bold text-white dark:bg-slate-300">
            Unit
          </th>
          <th
            v-for="result in results"
            :key="result.moduleTypeEnum"
            scope="col"
            :class="[
              'px-px-4 py-px-2 t-xs duration-fast ease-standard cursor-pointer bg-slate-400 text-center font-bold text-white transition-colors',
              hoveredPhase === getStage(result.moduleTypeEnum) && 'bg-slate-600 dark:bg-slate-300',
            ]">
            {{ getStage(result.moduleTypeEnum) }}
          </th>
        </tr>
      </thead>
    </template>

    <template #tbody>
      <tbody>
        <tr
          v-for="(indicator, index) in indicators"
          :key="indicator">
          <!-- Indicator name (sticky left) -->
          <td
            :class="[
              'z-sticky px-px-4 py-px-2 sticky left-0 overflow-hidden text-ellipsis',
              index % 2 === 0 ? 'bg-slate-100' : 'bg-slate-300',
              hoveredIndicator === indicator
                ? index % 2 === 0
                  ? 'bg-slate-400! text-white'
                  : 'bg-slate-600! text-white dark:bg-slate-300!'
                : '',
            ]"
            @mouseover="onHover(null, null)">
            {{ indicator }}
          </td>

          <!-- Unit -->
          <td
            :class="[
              'px-px-4 py-px-2 overflow-hidden text-ellipsis whitespace-nowrap',
              index % 2 === 0 ? 'bg-slate-100' : 'bg-slate-300',
              hoveredIndicator === indicator
                ? index % 2 === 0
                  ? 'bg-slate-400! text-white'
                  : 'bg-slate-600! text-white dark:bg-slate-300!'
                : '',
            ]"
            @mouseover="onHover(null, null)">
            {{ extractUnit(indicator) }}
          </td>

          <!-- Values per stage -->
          <td
            v-for="result in results"
            :key="result.moduleTypeEnum"
            :class="[
              'px-px-4 py-px-2 text-center',
              index % 2 === 0 ? 'bg-slate-0' : 'bg-slate-200',
              hoveredPhase === getStage(result.moduleTypeEnum) || hoveredIndicator === indicator
                ? index % 2 === 0
                  ? 'bg-slate-100 hover:bg-slate-400 hover:text-white'
                  : 'dark:hover:bg-slate-450 bg-slate-300 hover:bg-slate-600 hover:text-white'
                : '',
            ]"
            @mouseover="onHover(getStage(result.moduleTypeEnum) ?? null, indicator)">
            <span class="text-[0.70rem]">
              {{
                typeof getIndicatorValue(result, indicator) !== "number"
                  ? "-"
                  : format.decimalNumber(getIndicatorValue(result, indicator)) ?? "-"
              }}
            </span>
          </td>
        </tr>
      </tbody>
    </template>
  </Table>
</template>
